import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import { Box, Button, Typography } from "@mui/material";
import { ReactComponent as MicrophoneIcon } from "../assets/microphone.svg";
import {
  getViewerActionPaddingX,
  viewerActionButtonLabelSx,
  viewerPillActionButtonSx,
} from "./unityWebViewer/panelStyles";
import { useLocalization } from "../localization/LocalizationContext";

const AUDIO_RECORDING_FORMATS = [
  { mimeType: "audio/webm;codecs=opus", contentType: "audio/webm", extension: "webm" },
  { mimeType: "audio/webm", contentType: "audio/webm", extension: "webm" },
  { mimeType: "audio/mp4;codecs=mp4a.40.2", contentType: "audio/mp4", extension: "mp4" },
  { mimeType: "audio/mp4", contentType: "audio/mp4", extension: "mp4" },
  { mimeType: "video/mp4", contentType: "video/mp4", extension: "mp4" },
];

const EXTENSION_BY_CONTENT_TYPE = {
  "audio/webm": "webm",
  "video/webm": "webm",
  "audio/mp4": "mp4",
  "video/mp4": "mp4",
  "audio/mpeg": "mp3",
  "audio/mp3": "mp3",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/ogg": "ogg",
};

export const getBaseContentType = (mimeType = "") =>
  String(mimeType).split(";")[0].trim().toLowerCase();

export const getAudioExtensionForContentType = (mimeType) =>
  EXTENSION_BY_CONTENT_TYPE[getBaseContentType(mimeType)] || "";

export const getPreferredAudioRecordingFormat = () => {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return null;
  }

  const { MediaRecorder: BrowserMediaRecorder } = window;
  const canCheckSupport =
    typeof BrowserMediaRecorder.isTypeSupported === "function";

  if (!canCheckSupport) {
    return AUDIO_RECORDING_FORMATS.find(
      ({ contentType }) => contentType === "audio/mp4",
    );
  }

  return (
    AUDIO_RECORDING_FORMATS.find(({ mimeType }) =>
      BrowserMediaRecorder.isTypeSupported(mimeType),
    ) || { mimeType: "", contentType: "audio/webm", extension: "webm" }
  );
};

export const getAudioUploadMetadata = (recordingFormat) => {
  if (!recordingFormat) {
    return {};
  }

  return {
    audioContentType: recordingFormat.contentType,
    audioExtension: recordingFormat.extension,
    audioMimeType: recordingFormat.mimeType || recordingFormat.contentType,
  };
};

export const normalizeMicrophoneVolumeLevel = (average) => {
  const number = Number(average);
  if (!Number.isFinite(number)) return 0;
  return Math.min(1, Math.max(0, (number - 0.01) / 0.12));
};

const VoiceRecorder = forwardRef(
  (
    {
      doneRecording,
      assessmentComplete,
      stacked = false,
      hideUi = false,
      onVolumeChange,
    },
    ref,
  ) => {
    const { t } = useLocalization();
    const [recording, setRecording] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const gainNodeRef = useRef(null);
    const silenceTimerRef = useRef(null);
    const silenceIntervalRef = useRef(null); // new ref for interval
    const fileNameRef = useRef("Test.webm");
    const fileToUploadRef = useRef(null);
    const recordingFormatRef = useRef(null);
    const latestRecordedFormatRef = useRef(null);

    const streamRef = useRef(null);
    const sourceRef = useRef(null);
    const onVolumeChangeRef = useRef(onVolumeChange);

    useEffect(() => {
      onVolumeChangeRef.current = onVolumeChange;
    }, [onVolumeChange]);

    useImperativeHandle(ref, () => ({
      startRecording,
      stopRecording,
      uploadFileToS3,
    }));

    const emitVolumeLevel = (average) => {
      onVolumeChangeRef.current?.(normalizeMicrophoneVolumeLevel(average));
    };

    const startRecording = async (recordingId, recordingFormat) => {
      if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
        throw new Error("Audio recording is not supported in this browser.");
      }

      const selectedFormat =
        recordingFormat || getPreferredAudioRecordingFormat();
      recordingFormatRef.current = selectedFormat;
      latestRecordedFormatRef.current = null;

      const initialExtension = selectedFormat?.extension || "webm";
      fileNameRef.current = `${recordingId}.${initialExtension}`;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; // keep a handle so we can stop tracks later

      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      sourceRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current.connect(analyserRef.current);
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.gain.value = 0;
      analyserRef.current.connect(gainNodeRef.current);
      gainNodeRef.current.connect(audioContextRef.current.destination);

      const recorderOptions = selectedFormat?.mimeType
        ? { mimeType: selectedFormat.mimeType }
        : undefined;
      mediaRecorderRef.current = new window.MediaRecorder(stream, recorderOptions);
      const recorderContentType =
        getBaseContentType(mediaRecorderRef.current.mimeType) ||
        selectedFormat?.contentType ||
        "audio/webm";
      const recorderExtension =
        getAudioExtensionForContentType(recorderContentType) ||
        selectedFormat?.extension ||
        "webm";
      const actualFormat = {
        mimeType: mediaRecorderRef.current.mimeType || selectedFormat?.mimeType || recorderContentType,
        contentType: recorderContentType,
        extension: recorderExtension,
      };
      recordingFormatRef.current = actualFormat;
      fileNameRef.current = `${recordingId}.${recorderExtension}`;
      audioChunksRef.current = []; // reset
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.start();
      setRecording(true);
      monitorSilence();
    };


    // Optionally, you can also start monitoring in a useEffect:
    // useEffect(() => {
    //   if (recording) {
    //     monitorSilence();
    //   }
    // }, [recording]);

    const monitorSilence = () => {
      const analyser = analyserRef.current;
      if (!analyser) return;
      const dataArray = new Uint8Array(analyser.fftSize);

      const checkSilence = () => {
        analyser.getByteTimeDomainData(dataArray);
        const sum = dataArray.reduce(
          (acc, value) => acc + (value - 128) ** 2,
          0
        );
        const average = Math.sqrt(sum / dataArray.length) / 128;
        emitVolumeLevel(average);

        // If volume is below threshold, start the silence timer
        if (average < 0.01) {
          if (silenceTimerRef.current === null) {
            silenceTimerRef.current = setTimeout(() => {
              stopRecording();
            }, 4000);
          }
        } else {
          // If noise is detected, clear the timer
          if (silenceTimerRef.current !== null) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        }
      };

      // Check silence every 100ms
      silenceIntervalRef.current = setInterval(checkSilence, 100);
    };

    const stopRecording = () => {
      // stop silence checks immediately
      if (silenceIntervalRef.current) {
        clearInterval(silenceIntervalRef.current);
        silenceIntervalRef.current = null;
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }

      if (!mediaRecorderRef.current) {
        onVolumeChangeRef.current?.(0);
        return;
      }

      mediaRecorderRef.current.onstop = async () => {
        const recordedFormat = recordingFormatRef.current || {
          contentType: "audio/webm",
          extension: "webm",
          mimeType: "audio/webm",
        };
        // build the blob
        const blob = new Blob(audioChunksRef.current, {
          type: recordedFormat.contentType,
        });
        audioChunksRef.current = []; // release chunk refs ASAP

        // create URL for preview (if you need it)
        const url = URL.createObjectURL(blob);
        setAudioUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev); // revoke any previous URL
          return url;
        });

        // file for upload
        const file = new File([blob], fileNameRef.current, {
          type: recordedFormat.contentType,
        });
        fileToUploadRef.current = file;
        latestRecordedFormatRef.current = recordedFormat;

        // release blob reference (the File still holds data)
        // eslint-disable-next-line no-unused-vars
        // blob = null; // (optional in JS; GC will collect once unreferenced)

        await cleanupMedia();
        setRecording(false);
        doneRecording();
      };

      // ensure final dataavailable fires on some browsers
      try {
        mediaRecorderRef.current.requestData?.();
      } catch {}
      mediaRecorderRef.current.stop();
    };


    const uploadFileToS3 = async (presignedUrl, options = {}) => {
      const { suppressAssessmentComplete = false, onUploaded } = options;
      const fileToUpload = fileToUploadRef.current;

      if (!fileToUpload) {
        throw new Error("No recorded audio file is available for upload");
      }

      try {
        const response = await axios.put(presignedUrl, fileToUpload, {
          headers: {
            "Content-Type": fileToUpload.type || "application/octet-stream",
          },
        });

        if (response.status === 200) {
          // release memory now that server has it
          if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
          }
          fileToUploadRef.current = null;

          if (!suppressAssessmentComplete) {
            assessmentComplete?.();
          }
          if (typeof onUploaded === "function") {
            onUploaded({
              file: fileToUpload,
              recordingFormat: latestRecordedFormatRef.current,
            });
          }
        } else {
          console.error("File upload failed:", response.data);
        }
      } catch (error) {
        console.error(
          "Error uploading file:",
          error.response ? error.response.data : error.message
        );
        throw error;
      }
    };


    const cleanupMedia = async () => {
      // stop mic tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      // disconnect audio nodes
      try {
        sourceRef.current?.disconnect();
      } catch {}
      try {
        analyserRef.current?.disconnect();
      } catch {}
      try {
        gainNodeRef.current?.disconnect();
      } catch {}
      sourceRef.current = null;
      analyserRef.current = null;
      gainNodeRef.current = null;

      // close audio context
      if (audioContextRef.current) {
        try {
          await audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }

      // clear recorder + handlers
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current = null;
      }

      // clear timers
      if (silenceIntervalRef.current) {
        clearInterval(silenceIntervalRef.current);
        silenceIntervalRef.current = null;
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }

      onVolumeChangeRef.current?.(0);
    };


    if (!recording || hideUi) {
      return null;
    }

    return (
      <Box
        sx={{
          position: stacked ? "relative" : "absolute",
          bottom: stacked
            ? "auto"
            : "calc(env(safe-area-inset-bottom, 0px) + var(--panel-horizontal-offset, var(--panel-left-offset, 20px)))",
          right: stacked
            ? "auto"
            : "calc(env(safe-area-inset-right, 0px) + var(--panel-horizontal-offset, var(--panel-left-offset, 20px)))",
          width: stacked ? "100%" : "var(--panel-width-standard, 250px)",
          maxWidth: stacked
            ? "100%"
            : "calc(100vw - var(--panel-horizontal-offset, var(--panel-left-offset, 20px)) - var(--panel-horizontal-offset, var(--panel-left-offset, 20px)))",
          backgroundColor: "var(--surface-bg, rgba(9, 12, 18, 0.62))",
          boxShadow: "var(--surface-shadow, 0 14px 34px rgba(0, 0, 0, 0.34))",
          borderRadius: "var(--radius-lg, 14px)",
          backdropFilter:
            "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
          WebkitBackdropFilter:
            "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
          px: 2,
          py: 1.75,
          boxSizing: "border-box",
          zIndex: stacked ? "auto" : 1000,
          color: "#fff",
          pointerEvents: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            mb: 1.25,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 52,
              height: 52,
              flexShrink: 0,
              "@keyframes voicePulse": {
                "0%": { transform: "scale(0.92)", opacity: 0.55 },
                "65%": { transform: "scale(1.25)", opacity: 0 },
                "100%": { transform: "scale(1.25)", opacity: 0 },
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: -7,
                borderRadius: "50%",
                background: "var(--recording-pulse-bg, rgba(124, 253, 255, 0.24))",
                animation: "voicePulse 1.7s ease-out infinite",
              }}
            />
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: "var(--recording-mic-gradient)",
                boxShadow: "var(--recording-mic-shadow, 0 8px 20px rgba(60, 201, 194, 0.34))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MicrophoneIcon width={24} height={24} />
            </Box>
          </Box>

          <Box sx={{ textAlign: "left", minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                opacity: 0.74,
                letterSpacing: "0.11em",
                lineHeight: 1.1,
              }}
            >
              Voice Capture
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontSize: "1.05rem", lineHeight: 1.2, fontWeight: 650 }}
            >
              {t("guide.recording")}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={stopRecording}
          disabled={!recording}
          fullWidth
          sx={{
            ...viewerPillActionButtonSx,
            mt: 0.5,
            width: "100%",
            px: getViewerActionPaddingX(false),
            fontWeight: 700,
            letterSpacing: "0.02em",
            "&.Mui-disabled": {
              background: "var(--recording-cta-disabled, rgba(133, 245, 236, 0.35))",
              color: "var(--recording-cta-text-disabled, rgba(6, 25, 27, 0.5))",
            },
          }}
        >
          <Box
            component="span"
            className="control-label"
            sx={{
              ...viewerActionButtonLabelSx,
              fontSize: "14px",
            }}
          >
            {t("guide.recording_done")}
          </Box>
        </Button>
      </Box>
    );
  }
);

export default VoiceRecorder;
